import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, MagnifyingGlass, UploadSimple, ArrowsClockwise, FacebookLogo, Storefront,
  Eye, CaretLeft, CaretRight, Trash, DownloadSimple, Copy, CaretDown, DotsThreeVertical,
  Bell, Funnel, X, Clock,
} from "@phosphor-icons/react";
import AddLeadModal from "@/components/AddLeadModal";
import WhatsAppAlertModal from "@/components/WhatsAppAlertModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const STATUS_COLORS = {
  new: "#3B82F6", contacted: "#6B7280", initial: "#6366F1", fu1: "#F59E0B",
  fu2: "#F97316", fu3: "#EF4444", fu4: "#DC2626", fu5: "#991B1B", converted: "#10B981", lost: "#9CA3AF",
  quotation_sent: "#8B5CF6", qfu1: "#A78BFA", qfu2: "#8B5CF6", qfu3: "#7C3AED", qfu4: "#6D28D9", qfu5: "#4C1D95",
};
const STATUS_LABELS = {
  active: "Active Pipeline",
  new: "New", contacted: "Contacted", initial: "Initial", fu1: "FU-1", fu2: "FU-2", fu3: "FU-3", fu4: "FU-4", fu5: "FU-5", converted: "Converted", lost: "Lost",
  quotation_sent: "Quote Sent", qfu1: "Q-FU1", qfu2: "Q-FU2", qfu3: "Q-FU3", qfu4: "Q-FU4", qfu5: "Q-FU5",
};

const SOURCE_LABELS = {
  walk_in: "Walk In",
  meta: "Meta Ads",
  indiamart: "IndiaMART",
  telephonic: "Telephonic",
  csv_upload: "CSV Upload",
  google_form: "Google Form",
  other: "Other",
};

const REMINDER_LABELS = {
  has_reminder: "All Reminder Leads",
  pending: "Pending Reminders",
  due_this_month: "Due This Month",
  overdue: "Overdue Reminders",
  completed: "Completed Reminders",
};

const FOLLOWUP_LABELS = {
  due_today: "Due Today",
  failed: "Failed Follow-ups",
  queued: "Queued Follow-ups",
  sent: "Sent Follow-ups",
};

const FU_STAGES = ["initial", "fu1", "fu2", "fu3", "fu4", "fu5", "qfu1", "qfu2", "qfu3", "qfu4", "qfu5"];
const getFailedFollowups = (lead) => FU_STAGES.filter((s) => lead[`${s}_status`] === "failed");

const LEADS_FILTER_KEY = "leadpilot_leads_filters";

function loadStoredFilters() {
  try {
    const raw = sessionStorage.getItem(LEADS_FILTER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredFilters(filters) {
  try {
    sessionStorage.setItem(LEADS_FILTER_KEY, JSON.stringify(filters));
  } catch { }
}

function clearStoredFilters() {
  try {
    sessionStorage.removeItem(LEADS_FILTER_KEY);
  } catch { }
}

export default function Leads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSp = user?.role === "salesperson";

  // Check if returning from a lead details page (/leads/:id):
  const isReturningFromDetail = useMemo(() => {
    if (location.state?.fromMenuNav || location.state?.resetFilters) {
      return false;
    }
    if (location.state?.fromLeadDetail) {
      return true;
    }
    const prevRoute = sessionStorage.getItem("leadpilot_prev_route") || "";
    return /^\/leads\/[^/]+$/.test(prevRoute);
  }, [location.state]);

  const initialFilters = useMemo(() => {
    if (isReturningFromDetail) {
      const stored = loadStoredFilters();
      if (stored) {
        return stored;
      }
    }

    // Navigated from another menu or fresh visit: reset stored filters
    clearStoredFilters();

    const pc = searchParams.get("product_code") || "";
    const st = searchParams.get("status") || "";
    const src = searchParams.get("source") || "";
    const et = searchParams.get("entry_type") || "";
    const rem = searchParams.get("reminder") || "";
    const searchQ = searchParams.get("q") || "";
    let fu = searchParams.get("followup_status") || "";
    if (!fu && (searchParams.get("due_today") === "true" || searchParams.get("due") === "true")) {
      fu = "due_today";
    }
    const sp = searchParams.get("assigned_to") || "";
    const pg = Number(searchParams.get("page")) || 1;
    const ps = Number(searchParams.get("page_size")) || 25;
    const hasAny = Boolean(pc || st || src || et || rem || searchQ || fu || sp);

    return {
      q: searchQ,
      status: st,
      followupStatus: fu,
      source: src,
      entryType: et,
      reminderFilter: rem,
      productCode: pc,
      assignedTo: sp,
      page: pg,
      pageSize: ps,
      showFilters: hasAny,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on initial mount of this component instance

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialFilters.page || 1);
  const [pageSize, setPageSize] = useState(initialFilters.pageSize || 25);

  const totalPages = Math.ceil(total / pageSize) || 1;
  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  const [q, setQ] = useState(initialFilters.q || "");
  const [status, setStatus] = useState(initialFilters.status || "");
  const [followupStatus, setFollowupStatus] = useState(initialFilters.followupStatus || "");
  const [source, setSource] = useState(initialFilters.source || "");
  const [entryType, setEntryType] = useState(initialFilters.entryType || "");
  const [reminderFilter, setReminderFilter] = useState(initialFilters.reminderFilter || "");
  const [productCode, setProductCode] = useState(initialFilters.productCode || "");
  const [productsList, setProductsList] = useState([]);
  const [assignedTo, setAssignedTo] = useState(initialFilters.assignedTo || "");
  const [salespeople, setSalespeople] = useState([]);
  const [sourceStatus, setSourceStatus] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [duplicateLead, setDuplicateLead] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState({}); // { leadId: true }
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulking, setBulking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(initialFilters.showFilters || false);
  const fileRef = useRef(null);

  const activeFilterCount = [
    status,
    followupStatus,
    source,
    entryType,
    reminderFilter,
    assignedTo,
    productCode,
  ].filter(Boolean).length;

  // Sync state from location.search when location changes (from navigation/links/browser back)
  useEffect(() => {
    if (location.state?.fromMenuNav || location.state?.resetFilters) {
      clearAllFilters();
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (isReturningFromDetail) {
      const stored = loadStoredFilters();
      if (stored) {
        setQ(stored.q || "");
        setStatus(stored.status || "");
        setFollowupStatus(stored.followupStatus || "");
        setSource(stored.source || "");
        setEntryType(stored.entryType || "");
        setReminderFilter(stored.reminderFilter || "");
        setProductCode(stored.productCode || "");
        setAssignedTo(stored.assignedTo || "");
        setPage(stored.page || 1);
        if (stored.pageSize) setPageSize(stored.pageSize);
        setShowFilters(stored.showFilters || false);
        return;
      }
    }

    const pc = searchParams.get("product_code") || "";
    const st = searchParams.get("status") || "";
    const src = searchParams.get("source") || "";
    const et = searchParams.get("entry_type") || "";
    const rem = searchParams.get("reminder") || "";
    const searchQ = searchParams.get("q") || "";
    let fu = searchParams.get("followup_status") || "";
    if (!fu && (searchParams.get("due_today") === "true" || searchParams.get("due") === "true")) {
      fu = "due_today";
    }
    const sp = searchParams.get("assigned_to") || "";
    const pg = Number(searchParams.get("page")) || 1;
    const ps = Number(searchParams.get("page_size")) || 25;
    const hasAny = Boolean(pc || st || src || et || rem || searchQ || fu || sp);

    setQ(searchQ);
    setStatus(st);
    setFollowupStatus(fu);
    setSource(src);
    setEntryType(et);
    setReminderFilter(rem);
    setProductCode(pc);
    setAssignedTo(sp);
    setPage(pg);
    setPageSize(ps);
    if (hasAny) {
      setShowFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state, isReturningFromDetail]);

  // Save active filters to sessionStorage
  useEffect(() => {
    saveStoredFilters({
      q,
      status,
      followupStatus,
      source,
      entryType,
      reminderFilter,
      productCode,
      assignedTo,
      page,
      pageSize,
      showFilters,
    });
  }, [q, status, followupStatus, source, entryType, reminderFilter, productCode, assignedTo, page, pageSize, showFilters]);

  // Sync URL search parameters with active filters
  useEffect(() => {
    const next = new URLSearchParams();
    if (status) next.set("status", status);
    if (source) next.set("source", source);
    if (entryType) next.set("entry_type", entryType);
    if (reminderFilter) next.set("reminder", reminderFilter);
    if (productCode) next.set("product_code", productCode);
    if (followupStatus) next.set("followup_status", followupStatus);
    if (assignedTo) next.set("assigned_to", assignedTo);
    if (q) next.set("q", q);
    if (page > 1) next.set("page", String(page));
    if (pageSize !== 25) next.set("page_size", String(pageSize));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, source, entryType, reminderFilter, productCode, followupStatus, assignedTo, q, page, pageSize]);

  useEffect(() => {
    api.get("/products").then((r) => setProductsList(r.data || [])).catch(() => { });
  }, []);

  const clearAllFilters = () => {
    setQ("");
    setStatus("");
    setFollowupStatus("");
    setSource("");
    setEntryType("");
    setReminderFilter("");
    setProductCode("");
    setAssignedTo("");
    setPage(1);
    clearStoredFilters();
    setSearchParams({}, { replace: true });
  };

  const removeFilter = (filterKey) => {
    if (filterKey === "q") setQ("");
    if (filterKey === "status") setStatus("");
    if (filterKey === "followupStatus") setFollowupStatus("");
    if (filterKey === "source") setSource("");
    if (filterKey === "entryType") setEntryType("");
    if (filterKey === "reminderFilter") setReminderFilter("");
    if (filterKey === "productCode") setProductCode("");
    if (filterKey === "assignedTo") setAssignedTo("");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q) params.q = q;
      if (status) params.status = status;
      if (followupStatus) params.followup_status = followupStatus;
      if (source) params.source = source;
      if (entryType) params.entry_type = entryType;
      if (reminderFilter) params.reminder = reminderFilter;
      if (productCode) params.product_code = productCode;
      if (assignedTo) params.assigned_to = assignedTo;
      const r = await api.get("/leads", { params });
      setLeads(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [q, status, followupStatus, source, entryType, reminderFilter, productCode, assignedTo, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Reset page to 1 when search or filter values change, but NOT on initial restore
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPage(1);
  }, [q, status, followupStatus, source, entryType, reminderFilter, productCode, assignedTo]);
  useEffect(() => {
    if (!isSp) {
      api.get("/users/salespeople?department=lead").then(r => setSalespeople(r.data)).catch(() => { });
      api.get("/integrations/status").then(r => setSourceStatus(r.data)).catch(() => { });
    }
  }, [isSp]);

  const reassign = async (leadId, spId) => {
    if (!spId) return;
    try {
      await api.post(`/leads/${leadId}/assign`, { salesperson_id: spId });
      toast.success("Lead reassigned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleSelectAll = () => {
    const allIds = leads.map((l) => l.id);
    const allSelected = allIds.every((id) => selected[id]);
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(allIds.map((id) => [id, true])));
  };
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const bulkReassign = async () => {
    if (!bulkAssignee) return toast.error("Pick a salesperson");
    if (selectedIds.length === 0) return toast.error("No leads selected");
    setBulking(true);
    try {
      const r = await api.post("/leads/bulk-assign", { lead_ids: selectedIds, salesperson_id: bulkAssignee });
      toast.success(`Reassigned ${r.data.updated} leads`);
      setSelected({}); setBulkAssignee("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
    finally { setBulking(false); }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return toast.error("No leads selected");
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedIds.length} lead(s)? This action cannot be undone.`)) return;
    setBulking(true);
    try {
      const r = await api.post("/leads/bulk-delete", { lead_ids: selectedIds });
      toast.success(`Deleted ${r.data.deleted ?? selectedIds.length} lead(s)`);
      setSelected({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
    finally { setBulking(false); }
  };

  const canDelete = user?.role === "admin" || user?.role === "manager";

  const deleteSingleLead = async (leadId, leadName) => {
    if (!window.confirm(`Are you sure you want to permanently delete lead "${leadName || 'Unnamed'}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success(`Deleted lead "${leadName || 'Unnamed'}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const importMock = async (kind) => {
    setImporting(true);
    try {
      const r = await api.post(`/leads/import/${kind}`);
      if (r.data?.started) {
        toast.success(`${kind === "meta" ? "Meta" : "IndiaMART"} sync started — running in background`);
        // poll status until finished, max ~5 min
        pollSync(kind);
      } else if (r.data?.running) {
        toast.info(`${kind} sync is already running`);
        pollSync(kind);
      } else {
        // legacy array response (mock fallback fast path)
        toast.success(`Imported ${Array.isArray(r.data) ? r.data.length : 0} new ${kind} leads`);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const pollSync = async (kind) => {
    const start = Date.now();
    const tick = async () => {
      try {
        const r = await api.get("/integrations/sync-status");
        const s = r.data[kind];
        if (!s) return;
        if (!s.running) {
          if (s.last_error) toast.error(`${kind} sync failed: ${s.last_error.slice(0, 120)}`);
          else toast.success(`${kind} sync complete: ${s.last_count} new leads`);
          load();
          return;
        }
        if (Date.now() - start > 5 * 60_000) return; // 5 min cap
        setTimeout(tick, 4000);
      } catch (err) {
        console.error("Sync poll failed:", err);
      }
    };
    setTimeout(tick, 3000);
  };

  const importCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const r = await api.post("/leads/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.length} leads from CSV`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV import failed");
    } finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (status) params.append("status", status);
      if (followupStatus) params.append("followup_status", followupStatus);
      if (source) params.append("source", source);
      if (entryType) params.append("entry_type", entryType);
      if (reminderFilter) params.append("reminder", reminderFilter);
      if (assignedTo) params.append("assigned_to", assignedTo);
      if (productCode) params.append("product_code", productCode);

      const response = await api.get(`/leads/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  // Live label based on source-configured status
  const liveLabel = (k, fallback) => {
    if (!sourceStatus) return `${fallback} (sample)`;
    const s = sourceStatus[k];
    return s?.configured ? `Sync ${fallback}` : `${fallback} (sample)`;
  };

  return (
    <div data-testid="leads-page">
      <WhatsAppAlertModal />
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B7280] mb-2">Pipeline</div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-[#111827]">Leads</h1>
        </div>
        <button onClick={() => { setDuplicateLead(null); setShowAdd(true); }} data-testid="add-lead-button"
          className="bg-[#002FA7] text-white font-bold px-4 sm:px-5 py-2.5 hover:bg-[#002280] transition-colors flex items-center gap-2 text-sm">
          <Plus size={16} weight="bold" /> <span className="hidden sm:inline">New Lead</span><span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Import strip (manager/admin only) */}
      {!isSp && (
        <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-4 mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B7280] mb-3 flex items-center justify-between">
            <span>Sync Lead Sources</span>
            <Link to="/integrations" className="text-[#002FA7] hover:underline normal-case tracking-normal text-xs">Manage integrations →</Link>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => importMock("indiamart")} disabled={importing} data-testid="import-indiamart-btn"
              className={`px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-50 border ${sourceStatus?.indiamart?.configured ? "bg-[#002FA7] text-white border-[#002FA7] hover:bg-[#002280]" : "bg-white border-[#D1D5DB] hover:bg-[#F3F4F6]"}`}>
              <Storefront size={16} weight="bold" /> {liveLabel("indiamart", "IndiaMART")}
            </button>
            <button onClick={() => importMock("meta")} disabled={importing} data-testid="import-meta-btn"
              className={`px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-50 border ${sourceStatus?.meta?.configured ? "bg-[#002FA7] text-white border-[#002FA7] hover:bg-[#002280]" : "bg-white border-[#D1D5DB] hover:bg-[#F3F4F6]"}`}>
              <FacebookLogo size={16} weight="bold" /> {liveLabel("meta", "Meta Ads")}
            </button>
            <label className="bg-white border border-[#D1D5DB] px-4 py-2 text-sm font-bold hover:bg-[#F3F4F6] flex items-center gap-2 cursor-pointer">
              <UploadSimple size={16} weight="bold" /> CSV
              <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="import-csv-input"
                onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} />
            </label>
          </div>
        </div>
      )}

      {/* Search, Filter Bar, and Actions */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7280]" weight="bold" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search leads by name, phone, email, city, company..."
            data-testid="lead-search-input"
            className="w-full pl-10 pr-9 py-2.5 border border-[#D1D5DB] bg-white text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] focus:outline-none placeholder:text-[#9CA3AF]"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827] p-1 rounded-sm hover:bg-gray-100 transition-colors"
              title="Clear search"
            >
              <X size={14} weight="bold" />
            </button>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {/* Filters Toggle Button */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3.5 py-2.5 text-sm font-bold border transition-colors flex items-center gap-2 ${showFilters || activeFilterCount > 0
              ? "bg-blue-50 border-[#002FA7] text-[#002FA7]"
              : "bg-white border-[#D1D5DB] text-[#374151] hover:bg-[#F3F4F6]"
              }`}
          >
            <Funnel size={16} weight={activeFilterCount > 0 ? "fill" : "bold"} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-[#002FA7] text-white text-[11px] px-1.5 py-0.5 rounded-full font-black leading-none">
                {activeFilterCount}
              </span>
            )}
            <CaretDown
              size={13}
              weight="bold"
              className={`transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`}
            />
          </button>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-3.5 py-2.5 text-sm font-bold border border-[#D1D5DB] bg-white text-[#374151] hover:bg-[#F3F4F6] disabled:opacity-50 flex items-center gap-2 transition-colors"
            data-testid="refresh-btn"
            title="Refresh leads list"
          >
            <ArrowsClockwise size={16} weight="bold" className={loading ? "animate-spin text-[#002FA7]" : ""} />
            <span className="hidden md:inline">Refresh</span>
          </button>

          {/* Export CSV Button */}
          <button
            type="button"
            onClick={exportCsv}
            className="px-3.5 py-2.5 text-sm font-bold border border-[#D1D5DB] bg-white text-[#374151] hover:bg-[#F3F4F6] flex items-center gap-2 transition-colors ml-auto sm:ml-0"
            data-testid="export-btn"
            title="Export filtered leads to CSV"
          >
            <DownloadSimple size={16} weight="bold" />
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">Export</span>
          </button>
        </div>
      </div>

      {/* Active Filter Badges Strip */}
      {(activeFilterCount > 0 || q) && (
        <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 bg-[#F9FAFB] border border-[#E5E7EB] text-xs">
          <span className="text-[10px] font-black uppercase tracking-wider text-[#6B7280] mr-1">
            Active:
          </span>

          {q && (
            <span className="inline-flex items-center gap-1.5 bg-white border border-[#D1D5DB] px-2.5 py-1 rounded-sm font-medium text-[#111827]">
              <span>Search: <strong>"{q}"</strong></span>
              <button type="button" onClick={() => setQ("")} className="text-[#9CA3AF] hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {status && (
            <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-sm font-medium text-[#002FA7]">
              <span>Stage: <strong>{STATUS_LABELS[status] || status}</strong></span>
              <button type="button" onClick={() => removeFilter("status")} className="text-blue-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {followupStatus && (
            <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-sm font-medium ${followupStatus === "failed" ? "bg-red-50 border-red-200 text-[#DC2626]" : "bg-blue-50 border-blue-200 text-[#002FA7]"
              }`}>
              <span>Follow-up: <strong>{FOLLOWUP_LABELS[followupStatus] || followupStatus}</strong></span>
              <button type="button" onClick={() => removeFilter("followupStatus")} className="text-gray-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {source && (
            <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-sm font-medium text-[#002FA7]">
              <span>Source: <strong>{SOURCE_LABELS[source] || source}</strong></span>
              <button type="button" onClick={() => removeFilter("source")} className="text-blue-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {entryType && (
            <span className="inline-flex items-center gap-1.5 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-sm font-medium text-[#7E22CE]">
              <span>Type: <strong>{entryType}</strong></span>
              <button type="button" onClick={() => removeFilter("entryType")} className="text-purple-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {reminderFilter && (
            <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-sm font-medium text-[#B45309]">
              <span>Reminder: <strong>{REMINDER_LABELS[reminderFilter] || reminderFilter}</strong></span>
              <button type="button" onClick={() => removeFilter("reminderFilter")} className="text-amber-500 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {assignedTo && (
            <span className="inline-flex items-center gap-1.5 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-sm font-medium text-[#7C3AED]">
              <span>Assigned: <strong>{salespeople.find(s => s.id === assignedTo)?.name || 'Salesperson'}</strong></span>
              <button type="button" onClick={() => removeFilter("assignedTo")} className="text-purple-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {productCode && (
            <span className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-sm font-medium text-[#4F46E5]">
              <span>Product: <strong>{productsList.find(p => String(p.productCode) === String(productCode))?.productName || "Filter Applied"}</strong></span>
              <button type="button" onClick={() => removeFilter("productCode")} className="text-indigo-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          <button
            type="button"
            onClick={clearAllFilters}
            className="ml-auto text-xs font-bold text-[#DC2626] hover:text-[#991B1B] hover:underline flex items-center gap-1 px-2 py-0.5"
          >
            <Trash size={12} weight="bold" /> Clear all
          </button>
        </div>
      )}

      {/* Expandable Filter Panel */}
      {showFilters && (
        <div className="bg-white border border-[#111827] p-4 mb-4 shadow-sm" data-testid="filter-panel">
          <div className="flex items-center justify-between pb-3 mb-4 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <Funnel size={16} weight="bold" className="text-[#002FA7]" />
              <span className="font-heading font-black text-sm tracking-tight text-[#111827]">
                Filter Leads
              </span>
              {activeFilterCount > 0 && (
                <span className="text-[11px] font-bold bg-[#002FA7] text-white px-1.5 py-0.2 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs font-bold text-[#DC2626] hover:text-[#991B1B] hover:underline flex items-center gap-1"
                >
                  <Trash size={12} weight="bold" /> Reset all
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="text-xs font-bold text-[#6B7280] hover:text-[#111827]"
              >
                Hide
              </button>
            </div>
          </div>

          {/* Quick Preset Buttons */}
          <div className="flex items-center gap-2 flex-wrap mb-4 pb-3 border-b border-[#F3F4F6]">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mr-1">Quick Presets:</span>
            <button
              type="button"
              onClick={() => setFollowupStatus(followupStatus === "due_today" ? "" : "due_today")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${followupStatus === "due_today"
                ? "bg-[#DC2626] text-white border-[#DC2626] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              <Clock size={12} weight="bold" /> Due Today
            </button>
            <button
              type="button"
              onClick={() => setReminderFilter(reminderFilter === "due_this_month" ? "" : "due_this_month")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${reminderFilter === "due_this_month"
                ? "bg-[#002FA7] text-white border-[#002FA7] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              <Bell size={12} weight="bold" /> Due This Month
            </button>
            <button
              type="button"
              onClick={() => setReminderFilter(reminderFilter === "overdue" ? "" : "overdue")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${reminderFilter === "overdue"
                ? "bg-[#DC2626] text-white border-[#DC2626] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              ⚠️ Overdue Reminders
            </button>
            <button
              type="button"
              onClick={() => setFollowupStatus(followupStatus === "failed" ? "" : "failed")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${followupStatus === "failed"
                ? "bg-[#DC2626] text-white border-[#DC2626] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              ⚠️ Failed Follow-ups
            </button>
            <button
              type="button"
              onClick={() => setStatus(status === "new" ? "" : "new")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${status === "new"
                ? "bg-[#002FA7] text-white border-[#002FA7] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              New Leads
            </button>
            <button
              type="button"
              onClick={() => setStatus(status === "quotation_sent" ? "" : "quotation_sent")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${status === "quotation_sent"
                ? "bg-[#7C3AED] text-white border-[#7C3AED] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              Quotes Sent
            </button>
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {/* 1. Lead Stage */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Lead Stage
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="filter-status"
                className={`w-full border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${status ? "border-[#002FA7] font-bold text-[#002FA7] bg-blue-50/50" : "border-[#D1D5DB]"
                  }`}
              >
                <option value="">All Lead Stages</option>
                <option value="active">Active Pipeline</option>
                {Object.entries(STATUS_LABELS).filter(([k]) => k !== "active").map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* 2. Follow-up State */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Follow-up State
              </label>
              <select
                value={followupStatus}
                onChange={(e) => setFollowupStatus(e.target.value)}
                data-testid="filter-followup-status"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${followupStatus === "failed" || followupStatus === "due_today"
                  ? "border-[#EF4444] text-[#DC2626] font-bold bg-red-50"
                  : followupStatus
                    ? "border-[#002FA7] text-[#002FA7] font-bold bg-blue-50"
                    : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                <option value="">All Follow-up States</option>
                <option value="due_today">⏰ Due Today</option>
                <option value="failed">⚠️ Failed Follow-ups</option>
                <option value="queued">⏳ Queued Follow-ups</option>
                <option value="sent">✓ Sent Follow-ups</option>
              </select>
            </div>

            {/* 3. Source / Channel */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Source / Channel
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                data-testid="filter-source"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${source ? "border-[#002FA7] text-[#002FA7] font-bold bg-blue-50/50" : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                <option value="">All Sources / Channels</option>
                <option value="walk_in">Walk In</option>
                <option value="meta">Meta Ads</option>
                <option value="indiamart">IndiaMART</option>
                <option value="telephonic">Telephonic</option>
                <option value="csv_upload">CSV Upload</option>
                <option value="google_form">Google Form</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Creation Type (Manual vs Fetched) */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Creation Type
              </label>
              <select
                value={entryType}
                onChange={(e) => setEntryType(e.target.value)}
                data-testid="filter-entry-type"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${entryType ? "border-[#7E22CE] text-[#7E22CE] font-bold bg-purple-50/50" : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                <option value="">All Creation Types</option>
                <option value="Manual">Manual Entry</option>
                <option value="Fetched">Fetched</option>
              </select>
            </div>

            {/* 4. Follow-up Reminder */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Follow-up Reminder
              </label>
              <select
                value={reminderFilter}
                onChange={(e) => setReminderFilter(e.target.value)}
                data-testid="filter-reminder"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${reminderFilter ? "border-[#D97706] text-[#B45309] font-bold bg-amber-50" : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                <option value="">All Leads (Reminders: Any)</option>
                <option value="has_reminder">🔔 All Reminder Leads</option>
                <option value="pending">⏳ Pending Reminders</option>
                <option value="due_this_month">📅 Due This Month</option>
                <option value="overdue">⚠️ Overdue Reminders</option>
                <option value="completed">✓ Completed Reminders</option>
              </select>
            </div>

            {/* 5. Product Filter */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Product Master
              </label>
              <select
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
                data-testid="filter-product"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${productCode ? "border-[#002FA7] text-[#002FA7] font-bold bg-blue-50/50" : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                <option value="">All Products</option>
                {productsList.map((p) => (
                  <option key={p.id || p.productCode} value={p.productCode}>
                    {p.productName}
                  </option>
                ))}
              </select>
            </div>

            {/* 6. Salesperson (admin / manager only) */}
            {!isSp && (
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                  Salesperson
                </label>
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  data-testid="filter-assigned"
                  className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${assignedTo ? "border-[#002FA7] text-[#002FA7] font-bold bg-blue-50/50" : "border-[#D1D5DB] bg-white text-[#374151]"
                    }`}
                >
                  <option value="">All Salespersons</option>
                  {salespeople.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.lead_assign_for ? `(${s.lead_assign_for})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {!isSp && selectedIds.length > 0 && (
        <div className="border border-[#111827] bg-[#111827] text-white p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
          <span className="font-bold text-sm" data-testid="bulk-count">{selectedIds.length} selected</span>
          <span className="text-xs text-[#9CA3AF]">Reassign to →</span>
          <select value={bulkAssignee} onChange={(e) => setBulkAssignee(e.target.value)} data-testid="bulk-assignee"
            className="bg-white text-[#111827] border border-[#D1D5DB] px-3 py-1.5 text-sm font-bold">
            <option value="">Pick salesperson</option>
            {salespeople.map(s => <option key={s.id} value={s.id}>{s.name} {s.lead_assign_for ? `(${s.lead_assign_for})` : ""}</option>)}
          </select>
          <button onClick={bulkReassign} disabled={bulking || !bulkAssignee} data-testid="bulk-reassign"
            className="bg-[#FF2A00] text-white font-bold px-4 py-1.5 text-sm hover:bg-[#E62700] disabled:opacity-50">
            {bulking ? "Reassigning..." : "Reassign"}
          </button>
          <div className="h-5 w-[1px] bg-gray-700 hidden sm:block mx-1"></div>
          <button onClick={bulkDelete} disabled={bulking} data-testid="bulk-delete"
            className="border border-[#EF4444] text-[#EF4444] font-bold px-4 py-1.5 text-sm hover:bg-[#EF4444] hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-50">
            <Trash size={16} weight="bold" /> {bulking ? "Deleting..." : "Delete Selected"}
          </button>
          <button onClick={() => setSelected({})} data-testid="bulk-clear"
            className="border border-[#374151] px-3 py-1.5 text-sm font-bold hover:bg-[#1F2937] ml-auto">
            Clear
          </button>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-[#111827] bg-[#F9FAFB]">
              {!isSp && (
                <th className="py-3 px-3 w-8">
                  <input type="checkbox" data-testid="select-all"
                    checked={leads.length > 0 && leads.every(l => selected[l.id])}
                    onChange={toggleSelectAll} />
                </th>
              )}
              {(isSp
                ? ["Name", "Phone / Email", "Source", "Status", "Created", "Actions"]
                : ["Name", "Phone / Email", "Source", "Status", "Assigned", "Created", "Actions"]
              ).map((h, i) => (
                <th key={`${h}-${i}`} className={`py-3 px-4 text-[10px] uppercase tracking-widest font-bold text-[#111827] ${h === "Actions" ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 ? (
              <tr><td colSpan={isSp ? 6 : 8} className="py-12 text-center text-sm text-[#6B7280]">No leads found.</td></tr>
            ) : leads.map(l => {
              const sp = salespeople.find(s => s.id === l.assigned_to);
              return (
                <tr key={l.id} className={`border-b border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer ${selected[l.id] ? "bg-blue-50" : ""}`} data-testid={`lead-row-${l.id}`}
                  onClick={(e) => { if (e.target.tagName !== "SELECT" && e.target.tagName !== "OPTION" && e.target.tagName !== "INPUT" && e.target.tagName !== "A") navigate(`/leads/${l.id}`); }}>
                  {!isSp && (
                    <td className="py-3 px-3 w-8">
                      <input type="checkbox" checked={!!selected[l.id]} onChange={() => toggleSelect(l.id)}
                        data-testid={`select-lead-${l.id}`} onClick={(e) => e.stopPropagation()} />
                    </td>
                  )}
                  <td className="py-3 px-4">
                    <Link to={`/leads/${l.id}`} onClick={(e) => e.stopPropagation()} className="font-bold text-sm text-[#002FA7] hover:underline">{l.name}</Link>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs text-[#6B7280] truncate max-w-[200px]">{l.product_interest || "—"}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    <div className="font-mono text-[#111827] font-semibold">{l.phone && l.phone !== "—" ? l.phone : "—"}</div>
                    {l.email && (
                      <div className="text-[11px] text-[#6B7280] truncate max-w-[190px] mt-0.5" title={l.email}>
                        {l.email}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs font-bold">
                    <div className="uppercase tracking-widest text-[#374151]">{(l.channel || l.source || "").replace("_", " ")}</div>
                    <span className={`inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
                      (l.entry_type === "Fetched" || (!l.entry_type && (["meta_ads", "meta"].includes(l.source) || l.channel === "meta" || (l.external_id && l.external_id.startsWith("meta:")) || (l.notes && l.notes.toLowerCase().includes("meta form:")))))
                        ? "bg-purple-50 text-purple-700 border border-purple-200"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                    }`}>
                      {(l.entry_type === "Fetched" || (!l.entry_type && (["meta_ads", "meta"].includes(l.source) || l.channel === "meta" || (l.external_id && l.external_id.startsWith("meta:")) || (l.notes && l.notes.toLowerCase().includes("meta form:"))))) ? "Fetched" : "Manual"}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border"
                        style={{ color: STATUS_COLORS[l.status], borderColor: STATUS_COLORS[l.status] }}>
                        {STATUS_LABELS[l.status]}
                      </span>
                      {l.status === 'lost' && l.lost_at && (
                        <span
                          title={`Marked as Lost on: ${new Date(l.lost_at).toLocaleString('en-IN')}`}
                          className="text-[10px] text-[#4B5563] font-mono px-1.5 py-0.5 bg-gray-100 border border-gray-200"
                        >
                          Lost: {new Date(l.lost_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {getFailedFollowups(l).length > 0 && (
                        <span
                          title={`Failed attempts: ${getFailedFollowups(l).map(s => s.toUpperCase()).join(", ")}`}
                          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#EF4444] text-[#DC2626] bg-red-50 inline-flex items-center gap-1"
                        >
                          ⚠️ Failed FU ({getFailedFollowups(l).length})
                        </span>
                      )}
                      {l.lost_reminder_date && (
                        <span
                          title={`Follow-up Reminder: ${l.lost_reminder_date} (${l.lost_reminder_status === 'completed' ? 'Completed' : 'Pending'})`}
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border inline-flex items-center gap-1 ${l.lost_reminder_status === 'completed'
                            ? 'border-[#10B981] text-[#059669] bg-green-50'
                            : 'border-[#D97706] text-[#B45309] bg-amber-50'
                            }`}
                        >
                          <Bell size={11} weight="bold" /> {l.lost_reminder_status === 'completed' ? 'Done' : 'Reminder'}
                        </span>
                      )}
                    </div>
                  </td>
                  {!isSp && (
                    <td className="py-3 px-4 text-sm" onClick={(e) => e.stopPropagation()}>
                      <select value={l.assigned_to || ""} onChange={(e) => reassign(l.id, e.target.value)}
                        data-testid={`reassign-lead-${l.id}`}
                        className="border border-[#D1D5DB] px-2 py-1 text-xs font-bold bg-white max-w-[160px]">
                        <option value="" disabled>— pick —</option>
                        {salespeople.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      {!l.assigned_to && <div className="text-[9px] text-[#EF4444] font-bold mt-0.5">Unassigned</div>}
                    </td>
                  )}
                  <td className="py-3 px-4 text-xs text-[#6B7280] font-mono">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          data-testid={`lead-actions-btn-${l.id}`}
                          className="inline-flex items-center gap-1 border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] hover:border-[#111827] text-[#111827] bg-white transition-colors focus:outline-none"
                        >
                          Actions <CaretDown size={12} weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-36 bg-white border border-[#111827] p-1 shadow-lg z-50">
                        <DropdownMenuItem
                          onClick={() => navigate(`/leads/${l.id}`)}
                          data-testid={`action-view-${l.id}`}
                          className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#111827] hover:bg-[#F3F4F6] cursor-pointer outline-none"
                        >
                          <Eye size={14} weight="bold" className="text-[#002FA7]" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setDuplicateLead(l); setShowAdd(true); }}
                          data-testid={`action-duplicate-${l.id}`}
                          className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#111827] hover:bg-[#F3F4F6] cursor-pointer outline-none"
                        >
                          <Copy size={14} weight="bold" className="text-[#6B7280]" /> Duplicate
                        </DropdownMenuItem>
                        {canDelete && (
                          <>
                            <DropdownMenuSeparator className="my-1 border-t border-[#E5E7EB]" />
                            <DropdownMenuItem
                              onClick={() => deleteSingleLead(l.id, l.name)}
                              data-testid={`action-delete-${l.id}`}
                              className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#EF4444] hover:bg-red-50 cursor-pointer outline-none"
                            >
                              <Trash size={14} weight="bold" /> Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {leads.length === 0 ? (
          <div className="border border-dashed border-[#D1D5DB] p-8 text-center text-sm text-[#6B7280]">No leads found.</div>
        ) : leads.map(l => {
          const sp = salespeople.find(s => s.id === l.assigned_to);
          return (
            <div key={l.id} data-testid={`lead-card-${l.id}`} className="border border-[#E5E7EB] bg-white p-4">
              <div className="flex justify-between items-start gap-3">
                {!isSp && (
                  <input type="checkbox" checked={!!selected[l.id]} onChange={() => toggleSelect(l.id)}
                    className="mt-1 w-4 h-4" onClick={(e) => e.stopPropagation()} />
                )}
                <Link to={`/leads/${l.id}`} className="min-w-0 flex-1 block">
                  <div className="font-bold text-base text-[#111827] truncate">{l.name}</div>
                  <div className="text-xs text-[#6B7280] truncate">{l.product_interest || "—"}</div>
                </Link>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border whitespace-nowrap"
                    style={{ color: STATUS_COLORS[l.status], borderColor: STATUS_COLORS[l.status] }}>
                    {STATUS_LABELS[l.status]}
                  </span>
                  {getFailedFollowups(l).length > 0 && (
                    <span
                      title={`Failed attempts: ${getFailedFollowups(l).map(s => s.toUpperCase()).join(", ")}`}
                      className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#EF4444] text-[#DC2626] bg-red-50 whitespace-nowrap"
                    >
                      ⚠️ Failed FU ({getFailedFollowups(l).length})
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-2 flex justify-between items-center text-xs text-[#6B7280]">
                <span className="font-mono">{l.phone}</span>
                <div className="flex items-center gap-1.5">
                  <span className="uppercase tracking-widest font-bold">{(l.channel || l.source || "").replace("_", " ")}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
                    (l.entry_type === "Fetched" || (!l.entry_type && (["meta_ads", "meta"].includes(l.source) || l.channel === "meta" || (l.external_id && l.external_id.startsWith("meta:")) || (l.notes && l.notes.toLowerCase().includes("meta form:")))))
                      ? "bg-purple-50 text-purple-700 border border-purple-200"
                      : "bg-blue-50 text-blue-700 border border-blue-200"
                  }`}>
                    {(l.entry_type === "Fetched" || (!l.entry_type && (["meta_ads", "meta"].includes(l.source) || l.channel === "meta" || (l.external_id && l.external_id.startsWith("meta:")) || (l.notes && l.notes.toLowerCase().includes("meta form:"))))) ? "Fetched" : "Manual"}
                  </span>
                </div>
              </div>
              {l.product_interest && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-[#374151] truncate">
                  <span>{l.product_interest}</span>
                </div>
              )}
              {!isSp && sp && <div className="mt-1 text-[10px] text-[#6B7280]">@ {sp.name}</div>}
              <div className="mt-3 flex justify-end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid={`lead-card-actions-${l.id}`}
                      className="inline-flex items-center gap-1 border border-[#111827] px-3 py-1.5 text-xs font-bold hover:bg-[#111827] hover:text-white transition-colors focus:outline-none"
                    >
                      Actions <CaretDown size={12} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36 bg-white border border-[#111827] p-1 shadow-lg z-50">
                    <DropdownMenuItem
                      onClick={() => navigate(`/leads/${l.id}`)}
                      className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#111827] hover:bg-[#F3F4F6] cursor-pointer outline-none"
                    >
                      <Eye size={14} weight="bold" className="text-[#002FA7]" /> View
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { setDuplicateLead(l); setShowAdd(true); }}
                      className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#111827] hover:bg-[#F3F4F6] cursor-pointer outline-none"
                    >
                      <Copy size={14} weight="bold" className="text-[#6B7280]" /> Duplicate
                    </DropdownMenuItem>
                    {canDelete && (
                      <>
                        <DropdownMenuSeparator className="my-1 border-t border-[#E5E7EB]" />
                        <DropdownMenuItem
                          onClick={() => deleteSingleLead(l.id, l.name)}
                          className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#EF4444] hover:bg-red-50 cursor-pointer outline-none"
                        >
                          <Trash size={14} weight="bold" /> Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-[#E5E7EB]" data-testid="pagination">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280]">
            <div className="font-mono" data-testid="pagination-info">
              Showing <b className="text-[#111827]">{startIdx}-{endIdx}</b> of <b className="text-[#111827]">{total}</b> leads
            </div>
            <div className="flex items-center gap-1.5 border-l border-[#E5E7EB] pl-3">
              <span className="text-[#6B7280] font-semibold">Per page:</span>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="border border-[#D1D5DB] px-2 py-1 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1} data-testid="page-first" title="First Page"
              className="border border-[#D1D5DB] px-2.5 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              «
            </button>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="page-prev"
              className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors">
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <div className="border border-[#111827] bg-[#111827] text-white px-3 py-1.5 text-xs font-bold font-mono" data-testid="page-current">
              {page} / {totalPages}
            </div>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="page-next"
              className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors">
              Next <CaretRight size={12} weight="bold" />
            </button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} data-testid="page-last" title="Last Page"
              className="border border-[#D1D5DB] px-2.5 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              »
            </button>
          </div>
        </div>
      )}

      {showAdd && (
        <AddLeadModal
          onClose={() => { setShowAdd(false); setDuplicateLead(null); }}
          salespeople={salespeople}
          role={user.role}
          onCreated={load}
          initialData={duplicateLead}
        />
      )}
    </div>
  );
}
